import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface Principal {
  firstName: string;
  lastName: string;
  title: string;
}

interface Address {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  website?: string;
}

interface ExtraFields {
  business_category?: string;
  date_established?: string;
  drivers_license?: string;
  shipping_same_as_billing?: string;
  shipping_address?: Address | null;
  invoicing_email_same?: string;
  invoicing_email?: string;
  accounts_payable_email?: string;
  principals?: Principal[];
  signature_date?: string;
  has_signature?: boolean;
  signature_data?: string | null;
}

interface Application {
  id: string;
  company_name: string;
  legal_name: string | null;
  contact_name: string;
  email: string;
  phone: string;
  billing_address: Address | null;
  business_type: string;
  federal_id_ein: string;
  drivers_license_number: string | null;
  resale_certificate_url: string | null;
  w9_url: string | null;
  out_of_state_cert_url: string | null;
  jotform_submission_id: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  rejection_reason: string | null;
  info_request_message: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  extra_fields?: ExtraFields;
  detected_territory_id: string | null;
  detected_sales_rep_id: string | null;
  referring_rep_id: string | null;
  territory_name?: string | null;
  sales_rep_name?: string | null;
  referring_rep_name?: string | null;
  referring_rep_code?: string | null;
  lead_source?: string | null;
  referring_rep_firm_id?: string | null;
  referring_rep_firm_name?: string | null;
}

interface Territory {
  id: string;
  name: string;
  states: string[];
}

const DISCOUNT_TIERS = [
  { value: 40, label: '40% Discount', description: 'New dealer (default)' },
  { value: 50, label: '50% Discount', description: 'Standard dealer' },
  { value: 55, label: '55% Discount', description: 'Preferred dealer' },
  { value: 60, label: '60% Discount', description: 'Premium dealer' },
  { value: 65, label: '65% Discount', description: 'Elite dealer' },
];

async function fetchDocument(appId: string, docType: 'resale' | 'w9' | 'out_of_state_cert' | 'signed_pdf'): Promise<{ url: string; type: string } | null> {
  const token = localStorage.getItem('admin_auth_token');
  try {
    const response = await fetch(
      `${API_BASE}/accounting/pending-applications/${appId}/document/${docType}`,
      { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' },
    );
    if (!response.ok) throw new Error('Failed to fetch document');
    const contentType = response.headers.get('content-type') || 'application/octet-stream';
    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: contentType });
    const url = URL.createObjectURL(blob);
    return { url, type: contentType };
  } catch {
    return null;
  }
}

function formatAddress(addr: Address | null | undefined): string {
  if (!addr) return '';
  const lines = [
    [addr.street, addr.street2].filter(Boolean).join(', '),
    [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
  ].filter(Boolean);
  return lines.join('\n');
}

function SectionHeader({ title, icon }: { title: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
      <span className="text-gray-500">{icon}</span>
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex items-start py-1.5">
      <span className="text-sm text-gray-500 w-32 flex-shrink-0">{label}</span>
      <span className={`text-sm text-gray-900 min-w-0 break-words ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-gray-300 italic">Not provided</span>}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    approved: 'bg-green-100 text-green-800 border-green-200',
    portal_granted: 'bg-blue-100 text-blue-800 border-blue-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    on_hold: 'bg-red-100 text-red-800 border-red-200',
    dismissed: 'bg-gray-100 text-gray-600 border-gray-200',
    info_requested: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${styles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
      {status.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function MissingIcon() {
  return (
    <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export default function DealerApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});

  // Document preview
  const [docPreview, setDocPreview] = useState<{ url: string; type: string; label: string } | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [discountRate, setDiscountRate] = useState(50);
  const [approveNotes, setApproveNotes] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('direct');
  const [selectedTerritory, setSelectedTerritory] = useState('');
  const [selectedLeadSource, setSelectedLeadSource] = useState('jotform_organic');
  const [salesReps, setSalesReps] = useState<{ id: string; first_name: string; last_name: string; email: string }[]>([]);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [rejectReason, setRejectReason] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [infoFlaggedItems, setInfoFlaggedItems] = useState<string[]>([]);

  useEffect(() => {
    loadApplication();
    loadSalesReps();
    loadTerritories();
  }, [id]);

  const loadSalesReps = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_BASE}/users/sales-rep/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const reps = await res.json();
        setSalesReps(reps || []);
      }
    } catch (err) {
      console.error('Failed to load sales reps:', err);
    }
  };

  const loadTerritories = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_BASE}/territories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTerritories(data || []);
      }
    } catch (err) {
      console.error('Failed to load territories:', err);
    }
  };

  const loadApplication = async () => {
    try {
      setLoading(true);
      const data = await api.accounting.getPendingApplication(id!);
      setApp(data);
      // Pre-populate detected values for approve modal
      if (data.detected_territory_id) setSelectedTerritory(data.detected_territory_id);
      // Rep firm application → always 'rep' account type
      if (data.referring_rep_firm_id) {
        setSelectedLeadSource('rep_firm_referral');
        setSelectedAccountType('rep');
        if (data.detected_sales_rep_id) setSelectedSalesRep(data.detected_sales_rep_id);
      } else if (data.referring_rep_id) {
        // Referring rep takes priority over territory-detected rep
        setSelectedSalesRep(data.referring_rep_id);
        setSelectedLeadSource('sales_rep_referral');
        setSelectedAccountType('rep');
      } else if (data.detected_sales_rep_id) {
        setSelectedSalesRep(data.detected_sales_rep_id);
      }
      if (data.lead_source && !data.referring_rep_id && !data.referring_rep_firm_id) {
        setSelectedLeadSource(data.lead_source);
        const houseSources = ['marketing', 'website', 'trade_show', 'jotform_organic'];
        const repSources = ['sales_rep_referral', 'rep_firm_referral'];
        if (houseSources.includes(data.lead_source)) setSelectedAccountType('house');
        else if (repSources.includes(data.lead_source)) setSelectedAccountType('rep');
        else setSelectedAccountType('direct');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPrefillUrl = (formId: string) => {
    if (!app) return '';
    const addr = app.billing_address || {};
    const params = new URLSearchParams();
    if (formId === '260577091112048') {
      if (app.company_name) params.set('nameOf3', app.company_name);
      if (app.federal_id_ein) params.set('taxpayerIdentification', app.federal_id_ein);
      if (addr.street) params.set('addressnumber9', addr.street);
      if (addr.city) params.set('cityState10', [addr.city, addr.state, addr.zip].filter(Boolean).join(', '));
    } else if (formId === '260577528145059') {
      if (app.company_name) params.set('nameOf3', app.company_name);
      if (app.phone) params.set('phoneArea4[full]', app.phone);
      if (addr.street) params.set('addressstreet5', addr.street);
      if (addr.city) params.set('cityState6', [addr.city, addr.state, addr.zip].filter(Boolean).join(', '));
    }
    const qs = params.toString();
    return `https://form.jotform.com/${formId}${qs ? '?' + qs : ''}`;
  };

  const copyPrefillLink = (formId: string, label: string) => {
    const url = buildPrefillUrl(formId);
    navigator.clipboard.writeText(url);
    setCopiedLink(label);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const viewDocument = async (docType: 'resale' | 'w9' | 'out_of_state_cert' | 'signed_pdf', label: string) => {
    setDocLoading(true);
    const result = await fetchDocument(id!, docType);
    setDocLoading(false);
    if (!result) {
      setError('Failed to load document');
      return;
    }
    setDocPreview({ url: result.url, type: result.type, label });
  };

  const startEdit = () => {
    if (!app) return;
    setEditForm({
      company_name: app.company_name || '',
      legal_name: app.legal_name || '',
      contact_name: app.contact_name || '',
      email: app.email || '',
      phone: app.phone || '',
      business_type: app.business_type || '',
      federal_id_ein: app.federal_id_ein || '',
      billing_street: app.billing_address?.street || '',
      billing_city: app.billing_address?.city || '',
      billing_state: app.billing_address?.state || '',
      billing_zip: app.billing_address?.zip || '',
      shipping_street: app.shipping_address?.street || app.extra_fields?.shipping_address?.street || '',
      shipping_city: app.shipping_address?.city || app.extra_fields?.shipping_address?.city || '',
      shipping_state: app.shipping_address?.state || app.extra_fields?.shipping_address?.state || '',
      shipping_zip: app.shipping_address?.zip || app.extra_fields?.shipping_address?.zip || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      setActionLoading(true);
      const updateData: any = {
        company_name: editForm.company_name,
        legal_name: editForm.legal_name || null,
        contact_name: editForm.contact_name,
        email: editForm.email,
        phone: editForm.phone,
        business_type: editForm.business_type,
        federal_id_ein: editForm.federal_id_ein,
        billing_address: {
          ...app?.billing_address,
          street: editForm.billing_street,
          city: editForm.billing_city,
          state: editForm.billing_state,
          zip: editForm.billing_zip,
        },
        shipping_address: {
          ...(app?.shipping_address || app?.extra_fields?.shipping_address || {}),
          street: editForm.shipping_street,
          city: editForm.shipping_city,
          state: editForm.shipping_state,
          zip: editForm.shipping_zip,
          country: 'United States',
        },
      };
      await api.accounting.updatePendingApplication(id!, updateData);
      setEditing(false);
      setSuccessMsg('Application updated successfully');
      await loadApplication();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      const result = await api.accounting.approveDealerApplication(id!, {
        discount_rate: discountRate,
        notes: approveNotes || undefined,
        sales_rep_id: selectedSalesRep || undefined,
        account_type: selectedAccountType,
        territory_id: selectedTerritory || undefined,
        lead_source: selectedLeadSource || undefined,
      });
      setSuccessMsg(result.message || 'Dealer approved successfully!');
      setShowApproveModal(false);
      await loadApplication();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) return;
    try {
      setActionLoading(true);
      await api.accounting.rejectDealerApplication(id!, { rejection_reason: rejectReason });
      setShowRejectModal(false);
      setSuccessMsg('Application rejected');
      await loadApplication();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoMessage && infoFlaggedItems.length === 0) return;
    try {
      setActionLoading(true);
      await api.accounting.requestInfoDealerApplication(id!, {
        info_request_message: infoMessage,
        flagged_items: infoFlaggedItems,
      });
      setShowInfoModal(false);
      setInfoMessage('');
      setInfoFlaggedItems([]);
      setSuccessMsg('Information request sent and email delivered to applicant');
      await loadApplication();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    try {
      setActionLoading(true);
      await api.accounting.reopenDealerApplication(id!);
      setSuccessMsg('Application reopened for review');
      await loadApplication();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await api.accounting.deleteDealerApplication(id!);
      navigate('/accounting/approvals/dealers');
    } catch (err: any) {
      setError(err.message);
      setShowDeleteModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Application not found.
      </div>
    );
  }

  const extra = app.extra_fields || {};
  const addr = app.billing_address;
  const isPending = app.status === 'pending' || app.status === 'info_requested' || app.status === 'portal_granted';
  const isOnHold = app.status === 'on_hold' || app.status === 'rejected';
  const canDelete = app.status !== 'approved';

  // Parse flagged items from info_request_message (stored as JSON)
  let parsedInfoData: { message?: string; flaggedItems?: string[]; requestedAt?: string; requestedBy?: string } = {};
  if (app.info_request_message) {
    try {
      parsedInfoData = JSON.parse(app.info_request_message);
    } catch {
      // Legacy plain string — wrap it
      parsedInfoData = { message: app.info_request_message, flaggedItems: [] };
    }
  }
  const activeFlaggedItems = app.status === 'info_requested' ? (parsedInfoData.flaggedItems || []) : [];

  // Compliance checklist
  const hasResale = !!app.resale_certificate_url;
  const hasW9 = !!app.w9_url;
  const hasOutOfStateCert = !!app.out_of_state_cert_url;
  const hasEin = !!app.federal_id_ein;
  const hasAddress = !!(addr?.street && addr?.city && addr?.state);
  const isOutOfState = addr?.state && addr.state.toUpperCase() !== 'TX' && addr.state.toUpperCase() !== 'TEXAS';
  const complianceItems = [
    { label: 'State Tax Exempt / Resale Certificate', key: 'resale_cert', done: hasResale, flagged: activeFlaggedItems.includes('resale_cert') },
    { label: 'W-9 Form', key: 'w9', done: hasW9, flagged: activeFlaggedItems.includes('w9') },
    ...(isOutOfState || hasOutOfStateCert ? [{ label: 'Out-of-State Resale Certificate', key: 'out_of_state_cert', done: hasOutOfStateCert, flagged: activeFlaggedItems.includes('out_of_state_cert') }] : []),
    { label: 'Federal EIN', key: 'ein', done: hasEin, flagged: activeFlaggedItems.includes('ein') },
    { label: 'Business Address', key: 'address', done: hasAddress, flagged: activeFlaggedItems.includes('address') },
  ];
  const flaggedCount = complianceItems.filter(c => c.flagged).length;
  const complianceScore = complianceItems.filter(c => c.done && !c.flagged).length;

  return (
    <div className="max-w-4xl mx-auto pb-8">
      {/* Back button */}
      <button
        onClick={() => navigate('/accounting/approvals/dealers')}
        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Applications
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{app.company_name || 'Unnamed Company'}</h1>
            <p className="text-gray-500 text-sm mt-1">
              Submitted {new Date(app.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {app.reviewed_at && (
                <span> &middot; Reviewed {new Date(app.reviewed_at).toLocaleDateString()}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={app.status} />
            {isPending && !editing && (
              <button
                onClick={startEdit}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Compliance Quick Check */}
        <div className={`mt-4 p-3 rounded-lg ${flaggedCount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Compliance Checklist</span>
            {flaggedCount > 0 ? (
              <span className="text-xs font-bold text-red-600">{flaggedCount} item{flaggedCount > 1 ? 's' : ''} need{flaggedCount === 1 ? 's' : ''} correction</span>
            ) : (
              <span className={`text-xs font-bold ${complianceScore === complianceItems.length ? 'text-green-600' : 'text-amber-600'}`}>
                {complianceScore}/{complianceItems.length} Complete
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            {complianceItems.map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                {item.flagged ? (
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                ) : item.done ? <CheckIcon /> : <MissingIcon />}
                <span className={`text-xs ${item.flagged ? 'text-red-600 font-semibold' : item.done ? 'text-green-700' : 'text-gray-400'}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Banners */}
      {app.rejection_reason && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          <p className="font-semibold text-sm">Rejection Reason</p>
          <p className="text-sm mt-1">{app.rejection_reason}</p>
        </div>
      )}
      {app.info_request_message && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm">Information Requested</p>
            {parsedInfoData.requestedBy && (
              <span className="text-xs text-blue-500">by {parsedInfoData.requestedBy}{parsedInfoData.requestedAt ? ` — ${new Date(parsedInfoData.requestedAt).toLocaleDateString()}` : ''}</span>
            )}
          </div>
          {parsedInfoData.flaggedItems && parsedInfoData.flaggedItems.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {parsedInfoData.flaggedItems.map((f: string) => {
                const labels: Record<string, string> = { resale_cert: 'State Tax Exempt / Resale Certificate', w9: 'W-9 Form', out_of_state_cert: 'Out-of-State Resale Certificate', ein: 'Federal EIN', address: 'Business Address' };
                return (
                  <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium border border-amber-200">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01" /></svg>
                    {labels[f] || f}
                  </span>
                );
              })}
            </div>
          )}
          {parsedInfoData.message && <p className="text-sm mt-2 break-words overflow-hidden">{parsedInfoData.message}</p>}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-4">
          {/* Contact & Business Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Contact & Business" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            } />
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Company Name (DBA)</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" value={editForm.company_name} onChange={e => setEditForm({...editForm, company_name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Legal Entity Name</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" value={editForm.legal_name} onChange={e => setEditForm({...editForm, legal_name: e.target.value})} placeholder="e.g. Walker Designs, Inc." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Contact Name</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" value={editForm.contact_name} onChange={e => setEditForm({...editForm, contact_name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Business Type</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" value={editForm.business_type} onChange={e => setEditForm({...editForm, business_type: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Federal EIN</label>
                  <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" value={editForm.federal_id_ein} onChange={e => setEditForm({...editForm, federal_id_ein: e.target.value})} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <div>
                  <InfoRow label="Contact Name" value={app.contact_name} />
                  <InfoRow label="Email" value={app.email} />
                  <InfoRow label="Phone" value={app.phone} />
                  {addr?.website && <InfoRow label="Website" value={addr.website} />}
                </div>
                <div>
                  {app.legal_name && <InfoRow label="Legal Entity Name" value={app.legal_name} />}
                  <InfoRow label="Business Type" value={app.business_type} />
                  {extra.business_category && <InfoRow label="Category" value={extra.business_category} />}
                  {extra.date_established && <InfoRow label="Established" value={extra.date_established} />}
                </div>
              </div>
            )}
          </div>

          {/* Addresses */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Addresses" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            } />
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Billing Address</p>
                {editing ? (
                  <div className="space-y-2">
                    <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Street" value={editForm.billing_street} onChange={e => setEditForm({...editForm, billing_street: e.target.value})} />
                    <div className="grid grid-cols-3 gap-2">
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="City" value={editForm.billing_city} onChange={e => setEditForm({...editForm, billing_city: e.target.value})} />
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="State" value={editForm.billing_state} onChange={e => setEditForm({...editForm, billing_state: e.target.value})} />
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="ZIP" value={editForm.billing_zip} onChange={e => setEditForm({...editForm, billing_zip: e.target.value})} />
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-900 whitespace-pre-line">
                    {formatAddress(addr) || <span className="text-gray-300 italic">Not provided</span>}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">
                  Shipping Address
                  {!editing && extra.shipping_same_as_billing === 'Yes' && (
                    <span className="ml-2 text-green-600 font-normal">(Same as billing)</span>
                  )}
                </p>
                {editing ? (
                  <div className="space-y-2">
                    <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Street" value={editForm.shipping_street} onChange={e => setEditForm({...editForm, shipping_street: e.target.value})} />
                    <div className="grid grid-cols-3 gap-2">
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="City" value={editForm.shipping_city} onChange={e => setEditForm({...editForm, shipping_city: e.target.value})} />
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="State" value={editForm.shipping_state} onChange={e => setEditForm({...editForm, shipping_state: e.target.value})} />
                      <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="ZIP" value={editForm.shipping_zip} onChange={e => setEditForm({...editForm, shipping_zip: e.target.value})} />
                    </div>
                  </div>
                ) : extra.shipping_same_as_billing === 'Yes' ? (
                  <div className="text-sm text-gray-500 italic">Same as billing address</div>
                ) : extra.shipping_address ? (
                  <div className="text-sm text-gray-900 whitespace-pre-line">
                    {formatAddress(extra.shipping_address)}
                  </div>
                ) : (
                  <div className="text-sm text-gray-300 italic">Not provided</div>
                )}
              </div>
            </div>
          </div>

          {/* Territory, Lead Source & Sales Rep */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Territory & Sales Assignment" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            } />

            {/* Referral banner — show prominently if a rep referred this dealer */}
            {app.referring_rep_name && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800">
                  Referred by {app.referring_rep_name}
                  <span className="ml-2 text-xs font-normal text-green-600">
                    (code: {app.referring_rep_code}) — commission eligible
                  </span>
                </p>
              </div>
            )}

            {app.referring_rep_firm_name && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm font-medium text-purple-800">
                  Rep Firm Application — <strong>{app.referring_rep_firm_name}</strong>
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  This dealer applied through {app.referring_rep_firm_name}'s JotForm. Account type will default to "rep" with rep firm attribution.
                </p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Lead Source</p>
                <p className="text-sm font-medium text-gray-900">
                  {{
                    'jotform_organic': 'JotForm (Direct)',
                    'website': 'Website',
                    'marketing': 'Marketing / Ads',
                    'trade_show': 'Trade Show',
                    'sales_rep_referral': 'Sales Rep Referral',
                    'rep_firm_referral': 'Rep Firm Referral',
                    'existing_dealer': 'Dealer Referral',
                    'other': 'Other',
                  }[app.lead_source || ''] || app.lead_source || <span className="text-gray-400 italic font-normal">Not set</span>}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Territory</p>
                {app.territory_name ? (
                  <p className="text-sm font-medium text-gray-900">
                    {app.territory_name}
                    <span className="ml-2 text-xs text-blue-600 font-normal">
                      (from {app.billing_address?.state})
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    {app.billing_address?.state ? `No territory for "${app.billing_address.state}"` : 'No state on file'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Sales Rep</p>
                {app.referring_rep_name ? (
                  <p className="text-sm font-medium text-green-700">{app.referring_rep_name} (referral)</p>
                ) : app.sales_rep_name ? (
                  <p className="text-sm font-medium text-gray-900">{app.sales_rep_name} (territory)</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">No rep</p>
                )}
              </div>
            </div>
          </div>

          {/* Invoicing Emails */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Invoicing & Accounts Payable" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            } />
            <div className="grid grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Main Email" value={app.email} />
                <InfoRow
                  label="Use Main for Invoicing?"
                  value={extra.invoicing_email_same || 'Not specified'}
                />
              </div>
              <div>
                {extra.invoicing_email_same !== 'Yes' && extra.invoicing_email && (
                  <InfoRow label="Invoicing Email" value={extra.invoicing_email} />
                )}
                {extra.accounts_payable_email && (
                  <InfoRow label="AP Email" value={extra.accounts_payable_email} />
                )}
              </div>
            </div>
          </div>

          {/* Principals */}
          {extra.principals && extra.principals.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SectionHeader title="Business Principals" icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              } />
              <div className="space-y-2">
                {extra.principals.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {(p.firstName?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{p.firstName} {p.lastName}</p>
                      {p.title && <p className="text-xs text-gray-500">{p.title}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-4">
          {/* Documents */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Compliance Documents" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            } />
            <div className="space-y-3">
              {/* Signed Application PDF — JotForm's auto-generated complete
                  document (dealer agreement + W-9 + resale cert + signature
                  all in one). Pulled live via /submission/{id}/pdf. */}
              {app.jotform_submission_id && (
                <div className="p-3 rounded-lg border border-blue-300 bg-blue-50">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-semibold text-blue-900">Signed Dealer Application (full PDF)</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">Generated by JotForm Sign — includes the full application, W-9, resale cert, and dealer signature.</p>
                  <button
                    onClick={() => viewDocument('signed_pdf', 'Signed Dealer Application')}
                    className="mt-2 w-full text-center text-xs bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 font-medium"
                  >
                    View Signed PDF
                  </button>
                </div>
              )}

              {/* State Tax Exempt / Resale Certificate */}
              <div className={`p-3 rounded-lg border ${hasResale ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  {hasResale ? <CheckIcon /> : <MissingIcon />}
                  <span className="text-sm font-medium text-gray-900">State Tax Exempt / Resale Certificate</span>
                </div>
                {hasResale && (
                  <button
                    onClick={() => viewDocument('resale', 'State Tax Exempt / Resale Certificate')}
                    className="mt-2 w-full text-center text-xs bg-white border border-green-300 text-green-700 px-3 py-1.5 rounded hover:bg-green-50"
                  >
                    View Uploaded Document
                  </button>
                )}
                {/* Prefilled-form helper buttons removed Jun 5 — new combined
                    form generates the resale cert from inputs, dealer's actual
                    state-specific upload (q132) is what gets stored here.
                    The old standalone TX 01-339 form workflow is dead. */}
              </div>

              {/* W-9 */}
              <div className={`p-3 rounded-lg border ${hasW9 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  {hasW9 ? <CheckIcon /> : <MissingIcon />}
                  <span className="text-sm font-medium text-gray-900">W-9 Form</span>
                </div>
                {hasW9 && (
                  <button
                    onClick={() => viewDocument('w9', 'W-9 Form')}
                    className="mt-2 w-full text-center text-xs bg-white border border-green-300 text-green-700 px-3 py-1.5 rounded hover:bg-green-50"
                  >
                    View Uploaded Document
                  </button>
                )}
                {/* Prefilled-form button removed Jun 5 — W-9 is now generated by
                    the combined application form itself. Optional q116 upload is
                    captured here when dealer provides their pre-existing W-9. */}
              </div>

              {/* Out-of-State Resale Certificate — only show if uploaded */}
              {hasOutOfStateCert && (
                <div className="p-3 rounded-lg border border-green-200 bg-green-50">
                  <div className="flex items-center gap-2">
                    <CheckIcon />
                    <span className="text-sm font-medium text-gray-900">Out-of-State Resale Certificate</span>
                  </div>
                  <button
                    onClick={() => viewDocument('out_of_state_cert', 'Out-of-State Resale Certificate')}
                    className="mt-2 w-full text-center text-xs bg-white border border-green-300 text-green-700 px-3 py-1.5 rounded hover:bg-green-50"
                  >
                    View Document
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Identity */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <SectionHeader title="Identity Verification" icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            } />
            <InfoRow label="Driver's License" value={app.drivers_license_number || extra.drivers_license} mono />
            <InfoRow label="Federal EIN" value={app.federal_id_ein} mono />
          </div>

          {/* Signature */}
          {extra.has_signature && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SectionHeader title="Signature" icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              } />
              {extra.signature_data && (
                <div className="border border-gray-200 rounded p-2 bg-white">
                  <img src={extra.signature_data} alt="Applicant signature" className="w-full h-auto" />
                </div>
              )}
              {extra.signature_date && (
                <p className="text-xs text-gray-500 mt-2 text-center">Signed {extra.signature_date}</p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {isPending && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SectionHeader title="Actions" icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              } />
              {editing ? (
                <div className="space-y-2">
                  <button
                    onClick={saveEdit}
                    disabled={actionLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {actionLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    Cancel Edit
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {app.status === 'portal_granted' ? (
                    <>
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 text-center mb-1">
                        Portal access granted {app.portal_granted_at ? `on ${new Date(app.portal_granted_at).toLocaleDateString()}` : ''}
                      </div>
                      <button
                        onClick={() => setShowApproveModal(true)}
                        disabled={actionLoading}
                        className="w-full bg-green-600 text-white py-2.5 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                      >
                        Fully Approve (Documents Verified)
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={async () => {
                          if (!window.confirm('This creates a dealer login and sends credentials. The application stays in queue so accounting can verify documents (W-9, resale cert, etc).')) return;
                          try {
                            setActionLoading(true);
                            const result = await api.accounting.grantPortalAccess(id!, {
                              discount_rate: discountRate,
                              sales_rep_id: selectedSalesRep || undefined,
                              account_type: selectedAccountType,
                              territory_id: selectedTerritory || undefined,
                              lead_source: selectedLeadSource || undefined,
                            });
                            setSuccessMsg(result.message || 'Portal access granted!');
                            await loadApplication();
                          } catch (err: any) { setError(err.message); }
                          finally { setActionLoading(false); }
                        }}
                        disabled={actionLoading}
                        className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                      >
                        Create Login (Stays in Queue)
                      </button>
                      <button
                        onClick={() => setShowApproveModal(true)}
                        disabled={actionLoading}
                        className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                      >
                        Approve & Close
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={actionLoading}
                    className="w-full bg-red-50 text-red-700 border border-red-200 py-2 px-4 rounded-lg hover:bg-red-100 disabled:opacity-50 text-sm"
                  >
                    Reject Application
                  </button>
                  <button
                    onClick={() => setShowInfoModal(true)}
                    disabled={actionLoading}
                    className="w-full bg-blue-50 text-blue-700 border border-blue-200 py-2 px-4 rounded-lg hover:bg-blue-100 disabled:opacity-50 text-sm"
                  >
                    Request More Info
                  </button>
                </div>
              )}
            </div>
          )}

          {/* On Hold / Rejected Actions */}
          {isOnHold && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SectionHeader title="Actions" icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              } />
              <div className="space-y-2">
                <button
                  onClick={handleReopen}
                  disabled={actionLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                >
                  {actionLoading ? 'Reopening...' : 'Reopen Application'}
                </button>
              </div>
            </div>
          )}

          {/* Delete — available for any non-approved application */}
          {canDelete && (
            <div className="bg-white rounded-lg border border-red-200 p-5">
              <SectionHeader title="Danger Zone" icon={
                <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              } />
              <p className="text-xs text-gray-500 mb-3">Permanently delete this application. This cannot be undone.</p>
              <button
                onClick={() => setShowDeleteModal(true)}
                disabled={actionLoading}
                className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                Delete Application
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Approve Dealer Application</h3>
            <p className="text-sm text-gray-500 mb-4">
              Create a dealer account for <strong>{app.company_name}</strong>.
              Login credentials will be emailed to {app.email}.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Discount Tier</label>
              <div className="space-y-2">
                {DISCOUNT_TIERS.map((tier) => (
                  <label
                    key={tier.value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      discountRate === tier.value ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="discount"
                      value={tier.value}
                      checked={discountRate === tier.value}
                      onChange={() => setDiscountRate(tier.value)}
                      className="text-green-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{tier.label}</p>
                      <p className="text-xs text-gray-500">{tier.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {app?.detected_territory_id && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Auto-detected from {app?.billing_address?.state}:</strong>{' '}
                  Territory: {app?.territory_name || 'Unknown'}
                  {app?.sales_rep_name ? ` · Rep: ${app.sales_rep_name}` : ''}
                </p>
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source — Where did this dealer come from?</label>
                <select
                  value={selectedLeadSource}
                  onChange={(e) => {
                    const src = e.target.value;
                    setSelectedLeadSource(src);
                    // Auto-update account type based on source
                    const houseSources = ['marketing', 'website', 'trade_show', 'jotform_organic'];
                    const repSources = ['sales_rep_referral', 'rep_firm_referral'];
                    if (houseSources.includes(src)) setSelectedAccountType('house');
                    else if (repSources.includes(src)) setSelectedAccountType('rep');
                    else setSelectedAccountType('direct');
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="jotform_organic">JotForm (organic / direct application)</option>
                  <option value="website">Website "Become a Dealer"</option>
                  <option value="marketing">Marketing / Ads / Social Media</option>
                  <option value="trade_show">Trade Show / Expo</option>
                  <option value="sales_rep_referral">Sales Rep Referral</option>
                  <option value="rep_firm_referral">Rep Firm Referral</option>
                  <option value="existing_dealer">Existing Dealer Referral</option>
                  <option value="other">Other</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {selectedLeadSource === 'marketing' || selectedLeadSource === 'website' || selectedLeadSource === 'trade_show' || selectedLeadSource === 'jotform_organic'
                    ? 'No commission — house account (your marketing brought them in)'
                    : selectedLeadSource === 'sales_rep_referral' || selectedLeadSource === 'rep_firm_referral'
                    ? 'Commission eligible — rep gets credit for the referral'
                    : selectedLeadSource === 'existing_dealer'
                    ? 'Finder\'s fee may apply'
                    : ''}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Territory</label>
                <select
                  value={selectedTerritory}
                  onChange={(e) => setSelectedTerritory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="">None</option>
                  {territories.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <select
                    value={selectedAccountType}
                    onChange={(e) => setSelectedAccountType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="direct">Direct (no rep)</option>
                    <option value="house">House Account</option>
                    <option value="rep">Rep</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales Rep</label>
                  <select
                    value={selectedSalesRep}
                    onChange={(e) => setSelectedSalesRep(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">None</option>
                    {salesReps.map((rep) => (
                      <option key={rep.id} value={rep.id}>
                        {rep.first_name} {rep.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Any notes about this approval..."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                rows={2}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
              >
                {actionLoading ? 'Creating...' : 'Approve & Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reject Application</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 text-sm"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason || actionLoading}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Request More Information</h3>
            <p className="text-sm text-gray-500 mb-4">Select items that need correction and add a message. An email will be sent to the applicant.</p>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Flag Items for Correction</p>
              <div className="space-y-2">
                {[
                  { key: 'resale_cert', label: 'State Tax Exempt / Resale Certificate', hasIt: hasResale },
                  { key: 'w9', label: 'W-9 Form', hasIt: hasW9 },
                  ...(isOutOfState || hasOutOfStateCert ? [{ key: 'out_of_state_cert', label: 'Out-of-State Resale Certificate', hasIt: hasOutOfStateCert }] : []),
                  { key: 'ein', label: 'Federal EIN', hasIt: hasEin },
                  { key: 'address', label: 'Business Address', hasIt: hasAddress },
                ].map(item => (
                  <label key={item.key} className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded px-2 py-1.5 -mx-2 transition-colors">
                    <input
                      type="checkbox"
                      checked={infoFlaggedItems.includes(item.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setInfoFlaggedItems(prev => [...prev, item.key]);
                        } else {
                          setInfoFlaggedItems(prev => prev.filter(f => f !== item.key));
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 flex-1">{item.label}</span>
                    {item.hasIt ? (
                      <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Submitted</span>
                    ) : (
                      <span className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Missing</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <textarea
              value={infoMessage}
              onChange={(e) => setInfoMessage(e.target.value)}
              placeholder="Additional details for the applicant (optional if items are flagged)..."
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 text-sm"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowInfoModal(false); setInfoFlaggedItems([]); setInfoMessage(''); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestInfo}
                disabled={(!infoMessage && infoFlaggedItems.length === 0) || actionLoading}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {actionLoading ? 'Sending...' : `Send Request${infoFlaggedItems.length > 0 ? ` (${infoFlaggedItems.length} flagged)` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Application</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to permanently delete the application from <strong>{app.company_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {actionLoading ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {docPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={() => { URL.revokeObjectURL(docPreview.url); setDocPreview(null); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{docPreview.label}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={docPreview.url}
                  download={docPreview.label.replace(/\s+/g, '_')}
                  className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                >
                  Download
                </a>
                <button
                  onClick={() => { URL.revokeObjectURL(docPreview.url); setDocPreview(null); }}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              {docPreview.type.startsWith('image/') ? (
                <img src={docPreview.url} alt={docPreview.label} className="max-w-full max-h-[75vh] object-contain" />
              ) : (
                <iframe src={docPreview.url} className="w-full h-[75vh] border-0" title={docPreview.label} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for document fetch */}
      {docLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-700">Loading document...</span>
          </div>
        </div>
      )}
    </div>
  );
}
